import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderSearch } from 'lucide-react';
import { useSearchFolders, normalizeSearchFolderKind, type SearchFolder, type SearchFolderKind } from './useData';
import { useAccessibleAccounts } from '../emails/useData';
import { moduleConfig } from './config';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import { Button, PlusIcon } from '../../components/ui';
import { getDisplaySummaryStructured, getRulesForSummary } from './getSummarySentence';
import { sortSearchFolders } from './sortSearchFolders';
import { useModuleConfigStrings } from '../../i18n/useModuleConfigStrings';
import SmartFolderCard from './components/SmartFolderCard';
import SmartFoldersListHeader, { type SmartFolderKindCounts } from './components/SmartFoldersListHeader';

function parseSearchFolderAccountIds(folder: {
  accountId?: number | null;
  accountIds?: number[] | null;
}): number[] {
  if (folder.accountIds?.length) return folder.accountIds;
  if (folder.accountId != null) return [folder.accountId];
  return [];
}

function formatSearchFolderAccountsLabel(
  folder: { accountId?: number | null; accountIds?: number[] | null },
  accountById: Map<number, string>,
): string {
  const ids = parseSearchFolderAccountIds(folder);
  if (ids.length === 0) return 'All accounts';
  if (ids.length === 1) return accountById.get(ids[0]) ?? 'Unknown account';
  return `${ids.length} accounts`;
}

function getConditionCount(folder: SearchFolder): number {
  const rules = getRulesForSummary(folder);
  if (rules.length > 0) return rules.length;
  if (folder.search?.trim()) return 1;
  return 0;
}

function emptyKindCounts(): SmartFolderKindCounts {
  return {
    search: 0,
    inbox: 0,
    combined: 0,
    sent: 0,
    combinedSent: 0,
    mySent: 0,
    combinedMySent: 0,
  };
}

const List: React.FC = () => {
  const { colors } = useTheme();
  const strings = useModuleConfigStrings(moduleConfig.module.i18nKey, moduleConfig);
  const navigate = useNavigate();
  const { data: folders, isLoading: loading, remove: deleteFolder } = useSearchFolders();
  const { data: accessibleAccounts } = useAccessibleAccounts();
  const [deletingId, setDeletingId] = React.useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = React.useState<number | null>(null);

  const sortedFolders = useMemo(() => sortSearchFolders(folders), [folders]);

  const accountById = useMemo(() => {
    const map = new Map<number, string>();
    (accessibleAccounts ?? []).forEach((account) =>
      map.set(account.id, account.emailAddress ?? account.displayName ?? String(account.id)),
    );
    return map;
  }, [accessibleAccounts]);

  const kindCounts = useMemo(() => {
    const counts = emptyKindCounts();
    for (const folder of sortedFolders) {
      const kind = normalizeSearchFolderKind(folder.folderKind) as SearchFolderKind;
      counts[kind] += 1;
    }
    return counts;
  }, [sortedFolders]);

  const handleDeleteConfirm = async () => {
    const id = deleteConfirmId;
    if (id == null) return;
    setDeleteConfirmId(null);
    setDeletingId(id);
    try {
      await deleteFolder(id);
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className={cn('flex flex-col gap-4 p-4 md:p-6', colors.background)}>
        <div className={cn('h-28 animate-pulse rounded-xl border', colors.border, colors.surfaceHover)} />
        <div className="space-y-4">
          {[1, 2].map((key) => (
            <div key={key} className={cn('h-52 animate-pulse rounded-xl border', colors.border, colors.surfaceHover)} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-4 p-4 md:p-6', colors.background)}>
      <SmartFoldersListHeader
        totalFolders={sortedFolders.length}
        kindCounts={kindCounts}
        onAdd={() => navigate(moduleConfig.module.route.new)}
      />

      {sortedFolders.length === 0 ? (
        <div
          className={cn(
            'flex flex-col items-center justify-center rounded-xl border border-dashed px-6 py-16 text-center',
            colors.border,
            colors.surface,
          )}
        >
          <div
            className={cn(
              'mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border',
              colors.border,
              colors.surfaceHover,
              'text-indigo-600 dark:text-indigo-400',
            )}
          >
            <FolderSearch className="h-7 w-7" aria-hidden />
          </div>
          <p className={cn('text-base font-medium', colors.text)}>{strings.emptyNoData}</p>
          <p className={cn('mt-1 max-w-md text-sm', colors.textMuted)}>
            Create rule-based searches or mailbox shortcuts that appear under Emails.
          </p>
          <Button type="button" className="mt-6" onClick={() => navigate(moduleConfig.module.route.new)}>
            <PlusIcon className="h-5 w-5" />
            New smart folder
          </Button>
        </div>
      ) : (
        <ul className="grid gap-4">
          {sortedFolders.map((folder) => (
            <li key={folder.id}>
              <SmartFolderCard
                folder={folder}
                summary={getDisplaySummaryStructured(folder)}
                accountsLabel={formatSearchFolderAccountsLabel(folder, accountById)}
                conditionCount={getConditionCount(folder)}
                detailHref={moduleConfig.module.route.detail(folder.id)}
                editHref={moduleConfig.module.route.edit(folder.id)}
                onDelete={() => setDeleteConfirmId(folder.id)}
                deleting={deletingId === folder.id}
              />
            </li>
          ))}
        </ul>
      )}

      <ConfirmationDialog
        isOpen={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete smart folder"
        message="Delete this smart folder? It will be removed from the Emails sidebar."
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </div>
  );
};

export default List;
